import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

import { ref, watch, nextTick } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  length?: number;
  disabled?: boolean;
  size?: SizeType;
  status?: "" | "error" | "warning";
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OtpInput',
  props: {
    modelValue: { default: "" },
    length: { default: 6 },
    disabled: { type: Boolean, default: false },
    size: { default: "middle" },
    status: { default: "" }
  },
  emits: ["update:modelValue", "change", "complete"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const digits = ref<string[]>(Array.from({ length: props.length }, () => ""));
const inputRefs = ref<any[]>([]);

const setInputRef = (el: any, index: number) => {
  if (el) inputRefs.value[index] = el;
};

const syncFromModel = (value: string) => {
  const chars = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, props.length)
    .split("");
  digits.value = Array.from({ length: props.length }, (_, i) => chars[i] || "");
};

watch(
  () => props.modelValue,
  (val) => {
    const current = digits.value.join("");
    if (val !== current) syncFromModel(val ?? "");
  },
  { immediate: true }
);

watch(
  () => props.length,
  () => {
    digits.value = Array.from({ length: props.length }, (_, i) => digits.value[i] || "");
    syncFromModel(props.modelValue ?? "");
  }
);

const emitValue = () => {
  const value = digits.value.join("");
  emit("update:modelValue", value);
  emit("change", value);
  if (value.length === props.length && digits.value.every(Boolean)) {
    emit("complete", value);
  }
};

const focusAt = async (index: number) => {
  await nextTick();
  const target = inputRefs.value[index];
  const inputEl = target?.$el?.querySelector?.("input") || target?.input || target?.$el;
  inputEl?.focus?.();
  inputEl?.select?.();
};

const onInput = (index: number, val: string) => {
  const digit = String(val).replace(/\D/g, "").slice(-1);
  digits.value[index] = digit;
  emitValue();
  if (digit && index < props.length - 1) {
    focusAt(index + 1);
  }
};

const onKeydown = (index: number, e: KeyboardEvent) => {
  if (e.key === "Backspace") {
    if (digits.value[index]) {
      digits.value[index] = "";
      emitValue();
    } else if (index > 0) {
      digits.value[index - 1] = "";
      emitValue();
      focusAt(index - 1);
    }
    e.preventDefault();
  }

  if (e.key === "ArrowLeft" && index > 0) {
    e.preventDefault();
    focusAt(index - 1);
  }

  if (e.key === "ArrowRight" && index < props.length - 1) {
    e.preventDefault();
    focusAt(index + 1);
  }
};

const onPaste = (e: ClipboardEvent) => {
  e.preventDefault();
  const text = e.clipboardData?.getData("text") ?? "";
  const chars = text.replace(/\D/g, "").slice(0, props.length).split("");
  digits.value = Array.from({ length: props.length }, (_, i) => chars[i] || "");
  emitValue();
  focusAt(Math.min(chars.length, props.length - 1));
};

return (_ctx: any,_cache: any) => {
  const _component_a_input = _resolveComponent("a-input")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["ant-otp-input", { 'ant-otp-input--error': _ctx.status === 'error' }])
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.length, (_, index) => {
      return (_openBlock(), _createBlock(_component_a_input, {
        key: index,
        ref_for: true,
        ref: (el) => setInputRef(el, index),
        value: digits.value[index],
        disabled: _ctx.disabled,
        size: _ctx.size,
        status: _ctx.status,
        maxlength: 1,
        inputmode: "numeric",
        class: "ant-otp-input__box",
        "onUpdate:value": (val) => onInput(index, val),
        onKeydown: (e) => onKeydown(index, e),
        onPaste: onPaste
      }, null, 8, ["value", "disabled", "size", "status", "onUpdate:value", "onKeydown"]))
    }), 128))
  ], 2))
}
}

})